import { GoogleOAuthProvider } from '@react-oauth/google';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { PersistGate } from 'redux-persist/integration/react';
import Layout from './components/Layout/Layout';
import {
  GOOGLE_SIGNIN_ID,
  PUBLISHABLEKEY,
  RADAR_PUBLISHABLE_KEY,
  REACT_APP_GTM,
  RECAPTCHA_SITE_KEY,
} from './constants';
import { setRequestId, setVisitorId } from './helper/helperMethods';
import store, { persistor } from './redux/store/store';
import Routes from './routes/Routes/Routes';
import './App.scss';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from './components/ErrorBoundary';
import { brazeInititalize } from './helper/brazeHelper';
import Authentication from './Features/AccountSetup/components/UserAuthentication';
import { CampaignTriggerProvider } from './context/CampaignTriggerContext';
import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import Radar from 'radar-sdk-js';
import 'radar-sdk-js/dist/radar.css';

brazeInititalize();

try {
  if (RADAR_PUBLISHABLE_KEY) {
    Radar.initialize(RADAR_PUBLISHABLE_KEY);
  }
} catch (_) { }

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: any, query: any) => {
      const queryKey = query.queryKey[0];
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});
declare module 'react-query/types/react/QueryClientProvider' {
  export interface QueryClientProviderProps {
    children?: React.ReactNode;
  }
}

function App() {
  const stripePromise = loadStripe(PUBLISHABLEKEY);
  setVisitorId();
  setRequestId();

  useEffect(() => {
    try {
      TagManager.initialize({
        gtmId: REACT_APP_GTM,
      });
      ReactGA.initialize(import.meta.env.VITE_REACT_APP_GOOGLE_ANALYTICS_KEY);
    } catch (error) {
      console.error('Failed to initialize GTM:', error);
    }
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={<>loading...</>} persistor={persistor}>
        <div className="App">
          <GoogleOAuthProvider clientId={GOOGLE_SIGNIN_ID}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <CampaignTriggerProvider>
                  <ErrorBoundary>
                    <Elements stripe={stripePromise}>
                      <Layout>
                        <Routes />
                      </Layout>
                    </Elements>
                  </ErrorBoundary>
                  <ToastContainer />
                  <ReactQueryDevtools />
                </CampaignTriggerProvider>
              </BrowserRouter>
            </QueryClientProvider>
          </GoogleOAuthProvider>
        </div>
      </PersistGate>
    </Provider>
  );
}

export default App;